import React, { useState } from 'react';
import ga from 'react-ga';
import config from 'react-global-configuration';
import uuidv4 from 'uuid/v4';
import { NotificationContext, withNotificationContext } from '../../context/notification';
import { SessionContext, withSessionContext } from '../../context/session';
import { SupportContext, withSupportContext } from '../../context/support';
import { Collapse } from 'react-collapse'
import { Trans, withTranslation, WithTranslation } from "react-i18next"

function formatDateTime(dt: string) {
  if (!dt) return '-'
  const d = new Date(dt)
  return d.toISOString().slice(0, 19).replace('T', ' ')
}

interface RequestViewProps extends WithTranslation {
  request: any
  expanded: boolean
  supportContext: SupportContext
  onViewRequest: Function
}

function RequestView(props: RequestViewProps) {
  const [replyingToRequestId, setReplyingToRequestId] = useState(null)
  const [newMessageId, setNewMessageId] = useState(null)
  const [newMessageText, setNewMessageText] = useState('')

  const messages = Object.keys(props.request.messages).map(messageId => ({
    messageId: messageId,
    sender: props.request.messages[messageId].sender,
    createdAt: props.request.messages[messageId].createdAt,
    text: props.request.messages[messageId].text,
    status: props.request.messages[messageId].status,
  })).sort((a, b) => a.createdAt - b.createdAt)

  function handleResolveRequest(request: any, event: any) {
    event.preventDefault()
    if (!confirm(props.t('components_support_confirm_resolve_request', 'Are you sure you want to mark this case as resolved?'))) return
    props.supportContext.updateSupportRequest(request.requestId, {
      status: 'RESOLVED',
    })
      .then(() => {
        if (config.get('GA_TRACKING_ID')) ga.event({
          category: 'Support',
          action: 'Support Request Resolved',
        })
        props.supportContext.fetchSupportRequests()
      })
  }

  function handleReopenRequest (request: any, event: any) {
    event.preventDefault()
    props.supportContext.updateSupportRequest(request.requestId, {
      status: 'OPEN',
    })
      .then(() => {
        if (config.get('GA_TRACKING_ID')) ga.event({
          category: 'Support',
          action: 'Support Request Reopened',
        })
        props.supportContext.fetchSupportRequests()
      })
  }

  function handleDeleteRequest(request: any, event: any) {
    event.preventDefault()
    if (!confirm(props.t('components_support_confirm_delete_case', 'Really delete this case?'))) return
    props.supportContext.deleteSupportRequest(request.requestId)
      .then(() => {
        if (config.get('GA_TRACKING_ID')) ga.event({
          category: 'Support',
          action: 'Support Request Deleted',
        })
        props.supportContext.fetchSupportRequests()
      })
  }

  function handleDeleteMessage(request: any, message: any, event: any) {
    event.preventDefault()
    if (!confirm(props.t('components_support_confirm_delete_reply', 'Really delete this reply?'))) return
    props.supportContext.deleteSupportMessage(request.requestId, message.messageId)
      .then(() => {
        if (config.get('GA_TRACKING_ID')) ga.event({
          category: 'Support',
          action: 'Support Message Deleted',
        })
        props.supportContext.fetchSupportRequests()
      })
  }

  function handleAddMessage(request: any, event: any) {
    event.preventDefault()
    if (!newMessageId) return
    if (!newMessageText) return
    props.supportContext.updateSupportMessage(request.requestId, newMessageId, {
      text: newMessageText,
    })
      .then(() => {
        setReplyingToRequestId(null)
        setNewMessageId(null)
        setNewMessageText('')
        if (config.get('GA_TRACKING_ID')) ga.event({
          category: 'Support',
          action: 'Support Message Added',
        })
        props.supportContext.fetchSupportRequests()
      })
  }

  return (
    <div className={props.request.status === 'OPEN' ? "card" : "card"} key={props.request.requestId}>
      <div className="card-body mb-0 pb-0">
        <h5 className="card-title">
          <button className="btn btn-secondary float-right" onClick={(event: any) => { event.preventDefault(); props.onViewRequest(props.request.requestId)}}>{props.expanded ? props.t('components_support_button_go_back', 'Go Back') : props.t('components_support_button_view_case','View Case')}</button>
          <div>
            <Trans i18nKey="components_support_label_case">Case</Trans> {props.request.requestId.slice(0, 8)}
            {' '}
            <span className={props.request.status === 'OPEN' ? "badge badge-warning" : "badge badge-success"}>
              <Trans i18nKey={'components_support_badge_' + props.request.status}>{{status: props.request.status}}</Trans>
            </span>
          </div>
          <div className="text-secondary"><small><Trans i18nKey="components_support_label_created">Created</Trans> {formatDateTime(props.request.createdAt)}</small></div>
        </h5>
      </div>
      <Collapse isOpened={props.expanded}>
      <div className="card-body mt-0 pt-0">
        <div className="card-text">
          <div className="mt-0 mb-3">
            {props.request.status === 'OPEN' ?
              <button className="btn btn-success btn-sm" onClick={handleResolveRequest.bind(this, props.request)}><span className="fa fa-check"></span> <Trans i18nKey="components_support_button_mark_case_resolved">Mark Case Resolved</Trans></button>
              :
              <button className="btn btn-secondary btn-sm" onClick={handleReopenRequest.bind(this, props.request)}><span className="fa fa-check-o"></span> <Trans i18nKey="components_support_button_reopen_case">Reopen Case</Trans></button>
            }
            {' '}
            <button className="btn btn-danger btn-sm" onClick={handleDeleteRequest.bind(this, props.request)}><span className="fa fa-times"></span> <Trans i18nKey="components_support_button_delete_case">Delete Case</Trans></button>
          </div>

          <div className="alert alert-secondary">
            <div className="text-secondary"><Trans i18nKey="components_support_label_you_wrote">You wrote on</Trans> {formatDateTime(props.request.createdAt)}:</div>
            {props.request.text}
          </div>
        </div>
        {messages.length > 0 || props.request.status === 'OPEN' ?
          <div className="card-text">
            {messages.map(message => {
              return (
                <div key={message.messageId} style={{ marginBottom: '1em' }} className={message.sender === 'user' ? "alert alert-secondary" : "alert alert-info"}>
                  <div className="text-secondary">
                    <span className={message.status ? "fa fa-info-circle" : "fa fa-reply"}></span>
                    {' '}
                    { message.sender === 'user' && message.status === 'OPEN' ? <Trans i18nKey="components_support_label_you_reopened_this_case">You reopened this case on</Trans>
                    : message.sender === 'user' && message.status === 'RESOLVED' ? <Trans i18nKey="components_support_label_you_marked_this_case_as_resolved">you marked this case as resolved on</Trans>
                    : message.sender === 'user' ? <Trans i18nKey="components_support_label_you_wrote">You wrote on</Trans>
                    : message.sender !== 'user' && message.status === 'OPEN' ? <Trans i18nKey="components_support_label_support_reopened_this_case">Support reopened this case on</Trans>
                    : message.sender !== 'user' && message.status === 'RESOLVED' ? <Trans i18nKey="components_support_label_support_marked_this_case_as_resolved">Support marked this case as resolved on</Trans>
                    : <Trans i18nKey="components_support_label_support_replied">Support replied</Trans>}
                    {' '}
                    {formatDateTime(message.createdAt)}
                    {' '}
                    {message.sender === 'user' && message.text ? <a href="#" onClick={handleDeleteMessage.bind(this, props.request, message)}><span style={{ color: 'red' }} className="fa fa-times"></span></a> : null}
                  </div>
                  {message.text ? <div>
                    {message.text}
                  </div> : null}
                </div>
              )
            })}
            {replyingToRequestId === props.request.requestId ?
              <form onSubmit={handleAddMessage.bind(this, props.request)}>
                <div className="form-group">
                  <label><Trans i18nKey="components_support_button_add_reply">Add Reply</Trans></label>
                  <textarea className="form-control" value={newMessageText} onChange={(event: any) => setNewMessageText(event.target.value)} style={{ minHeight: '2em' }} />
                </div>
                <div>
                  <button type="submit" className="btn btn-primary"><Trans i18nKey="components_support_button_submit_reply">Submit Reply</Trans></button>
                  {' '}
                  <button type="button" className="btn btn-default btn-secondary" onClick={(_event: any) => setReplyingToRequestId(null)}><Trans i18nKey="components_button_cancel">Cancel</Trans></button>
                </div>
              </form>
              : props.request.status === 'OPEN' ?
                <a className="btn btn-primary btn-sm" href="#" onClick={(_event: any) => { setReplyingToRequestId(props.request.requestId); setNewMessageId(uuidv4()); setNewMessageText('') }}><Trans i18nKey="components_support_button_add_reply">Add Reply</Trans></a>
                : null}
          </div> : null}
      </div>
      </Collapse>
    </div>
  )
}

const RequestViewWrapper = withTranslation()(RequestView)

class SupportProps {
  sessionContext: SessionContext
  notificationContext: NotificationContext
  supportContext: SupportContext
  location: any
}

class SupportView extends React.Component<SupportProps> {
  state = {
    expandedRequestId: null,
    newRequestId: null,
    newRequestText: '',
    newRequestStatus: 'OPEN',
    loading: false,
  }

  componentDidMount() {
    if (config.get('GA_TRACKING_ID')) ga.event({
      category: 'Support',
      action: 'Support Opened',
    })
    this.handleRefresh()
  }

  handleRefresh = async (_event: any) => {
    this.setState({
      loading: true,
    })
    try {
      await this.props.supportContext.fetchSupportRequests()
      this.setState({
        loading: false,
      })
    } catch (err) {
      this.props.notificationContext.addErrorNotification(err)
      this.setState({
        loading: false,
      })
    }
  }

  handleCreateRequest = (event: any) => {
    event.preventDefault()
    if (this.state.newRequestId) return
    this.setState({
      newRequestId: uuidv4(),
      newRequestText: '',
      newRequestStatus: 'OPEN',
    })
  }

  handleSaveRequest = (event: any) => {
    event.preventDefault()
    if (!this.state.newRequestId) return
    if (!this.state.newRequestText) return
    this.props.supportContext.updateSupportRequest(this.state.newRequestId, {
      text: this.state.newRequestText,
      serviceId: config.get('SUPPORT_SERVICE_ID'),
      status: this.state.newRequestStatus,
    })
    .then(() => {
      if (config.get('GA_TRACKING_ID')) ga.event({
        category: 'Support',
        action: 'Support Request Created',
      })
      this.setState({
        newRequestId: null,
        newRequestText: '',
      })
      this.props.supportContext.fetchSupportRequests()
    })
    .catch((_err: any) => {
      if (config.get('GA_TRACKING_ID')) ga.event({
        category: 'Support',
        action: 'Support Request Failed',
      })
    })
  }


  render() {
    // const query = qs.parse(this.props.location.search.slice(1))

    return (
      <div>
        <div className="row flex-button-row mt-5">
          <div className="col-lg-6 flex-button-title">
            <h1><Trans i18nKey="components_support_title">Support &amp; Feedback</Trans></h1>
          </div>
          <div className="col-lg-6 flex-button-col">
          <button disabled={!!this.state.newRequestId} className="btn btn-primary" onClick={this.handleCreateRequest}><span className="fa fa-plus"></span> <Trans i18nKey="components_support_button_create_new_case">Create New Case</Trans></button>
            {' '}
            <button className="btn btn-default btn-secondary" onClick={this.handleRefresh}><span className={this.state.loading ? "fa fa-sync-alt fa-spin" : "fa fa-sync-alt"}></span> <Trans i18nKey="components_button_refresh">Refresh</Trans></button>
          </div>
        </div>

        {this.state.newRequestId ?
        <div>
          <form onSubmit={this.handleSaveRequest}>
            <div className="form-group">
              <label><Trans i18nKey="components_support_new_case_description">How can we help you?</Trans></label>
              <textarea className="form-control" onChange={(event) => this.setState({newRequestText: event.target.value})} value={this.state.newRequestText} style={{minHeight:'7em'}} placeholder={this.props.t('components_support_placeholder_please_describe', "Please describe your problem or enter feedback")}/>
            </div>
            <div className="radio">
              <label><input type="radio" value={'OPEN'} checked={this.state.newRequestStatus === 'OPEN'} onChange={(event) => this.setState({newRequestStatus: 'OPEN'})}/> <Trans i18nKey="components_support_label_want_response">I would like a response</Trans></label>
            </div>
            <div className="radio">
              <label><input type="radio" value={'RESOLVED'} checked={this.state.newRequestStatus === 'RESOLVED'} onChange={(event) => this.setState({newRequestStatus: 'RESOLVED'})}/> <Trans i18nKey="components_support_label_only_feedback">This is only feedback, I don't need a response</Trans></label>
            </div>
            <div>
              <button type="submit" className="btn btn-primary"><Trans i18nKey="components_support_button_open_case">Open Case</Trans></button>
              {' '}
              <button type="button" className="btn btn-default btn-secondary" onClick={(event) => this.setState({newRequestId:null})}><Trans i18nKey="components_button_cancel">Cancel</Trans></button>
            </div>
            <br/>
          </form>
        </div> : null}

        {!this.state.newRequestId && this.state.loading ?
          <div className="alert alert-secondary"><span className="fa fa-sync-alt fa-spin"></span> <Trans i18nKey="componetns_label_loading"><Trans i18nKey="components_label_loading">Loading...</Trans></Trans></div>
        :!this.state.newRequestId ?
        <div>
          {this.props.supportContext.requests.length > 0
          ? this.props.supportContext.requests.map((request: any) => {
            // if (this.state.expandedRequestId && this.state.expandedRequestId !== request.requestId) return <div></div>
            return <RequestViewWrapper key={request.requestId} request={request} expanded={this.state.expandedRequestId === request.requestId} supportContext={this.props.supportContext} onViewRequest={(requestId: string) => this.setState({ expandedRequestId: (this.state.expandedRequestId === requestId ? null : requestId) })} />
          })
          : <div className="alert alert-secondary"><Trans i18nKey="components_support_alert_no_cases_yet">You don't have any support cases yet.</Trans></div>}
        </div>
        : null}

      </div>
    )
  }
}

export const Support = withTranslation()(withNotificationContext(withSessionContext(withSupportContext(SupportView))))
